/**
 * HTML Email Generator
 * Converts EmailDocument state to valid table-based HTML email
 */

import {
  EmailDocument,
  EmailRow,
  EmailColumn,
  ContentBlock,
  TextBlock,
  ImageBlock,
  ButtonBlock,
  DividerBlock,
  SpacerBlock,
  FooterBlock,
  VaccineTableBlock,
  VaccineListType,
} from './types';

interface GeneratorOptions {
  practiceName?: string;
}

/**
 * Generate valid HTML email from EmailDocument
 */
export function generateEmailHtml(doc: EmailDocument, options: GeneratorOptions = {}): string {
  const { globalStyles } = doc;
  const { practiceName = '{{practice_name}}' } = options;

  // If no rows, return empty template
  if (doc.rows.length === 0) {
    return generateEmptyTemplate(globalStyles, practiceName);
  }

  const rowsHtml = doc.rows.map(row => generateRow(row, globalStyles.contentWidth)).join('\n');

  return `<!DOCTYPE html>
<html lang="en" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <!-- Prevent dark mode color inversion in email clients -->
  <meta name="color-scheme" content="light only">
  <meta name="supported-color-schemes" content="light only">
  <title>Email</title>
  <!--[if mso]>
  <noscript>
    <xml>
      <o:OfficeDocumentSettings>
        <o:AllowPNG/>
        <o:PixelsPerInch>96</o:PixelsPerInch>
      </o:OfficeDocumentSettings>
    </xml>
  </noscript>
  <![endif]-->
  <style>
    /* Force light mode - prevent dark mode color changes */
    :root {
      color-scheme: light only;
      supported-color-schemes: light only;
    }
    /* Apple Mail dark mode override */
    @media (prefers-color-scheme: dark) {
      .email-body,
      .email-wrapper,
      .email-content,
      .email-row {
        background-color: inherit !important;
      }
      .dark-mode-bg {
        background-color: inherit !important;
      }
    }
    /* Outlook dark mode override */
    [data-ogsc] .email-body,
    [data-ogsc] .email-wrapper,
    [data-ogsc] .email-content,
    [data-ogsc] .email-row,
    [data-ogsc] .dark-mode-bg {
      background-color: inherit !important;
    }
  </style>
</head>
<body class="email-body" style="margin: 0; padding: 0; font-family: ${globalStyles.fontFamily}; background-color: ${globalStyles.backgroundColor}; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%;">
  <table role="presentation" class="email-wrapper" width="100%" border="0" cellspacing="0" cellpadding="0" style="background-color: ${globalStyles.backgroundColor};">
    <tr>
      <td align="center" style="padding: 40px 20px;">
        <table role="presentation" class="email-content" width="${globalStyles.contentWidth}" border="0" cellspacing="0" cellpadding="0" style="max-width: ${globalStyles.contentWidth}px; width: 100%;">
${rowsHtml}
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

/**
 * Generate empty template placeholder
 */
function generateEmptyTemplate(globalStyles: EmailDocument['globalStyles'], practiceName: string): string {
  return `<!DOCTYPE html>
<html lang="en" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="color-scheme" content="light only">
  <meta name="supported-color-schemes" content="light only">
  <title>Email</title>
  <style>
    :root { color-scheme: light only; }
  </style>
</head>
<body class="email-body" style="margin: 0; padding: 0; font-family: ${globalStyles.fontFamily}; background-color: ${globalStyles.backgroundColor};">
  <table role="presentation" class="email-wrapper" width="100%" border="0" cellspacing="0" cellpadding="0" style="background-color: ${globalStyles.backgroundColor};">
    <tr>
      <td align="center" style="padding: 40px 20px;">
        <table role="presentation" class="email-content" width="${globalStyles.contentWidth}" border="0" cellspacing="0" cellpadding="0" style="max-width: ${globalStyles.contentWidth}px; width: 100%; background-color: #ffffff;">
          <tr>
            <td style="padding: 40px; text-align: center;">
              <p style="margin: 0; color: #64748b; font-size: 14px;">Start building your email by adding rows and content blocks.</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

/**
 * Generate a single row
 */
function generateRow(row: EmailRow, contentWidth: number): string {
  const columnsHtml = row.columns.map(col => generateColumn(col, row.columns.length, contentWidth)).join('\n');

  // Build background styles
  let backgroundStyle = `background-color: ${row.backgroundColor};`;
  if (row.backgroundImage) {
    const bgSize = row.backgroundSize || 'cover';
    const bgPosition = row.backgroundPosition || 'center';
    const bgRepeat = row.backgroundRepeat || 'no-repeat';
    backgroundStyle += ` background-image: url('${row.backgroundImage}'); background-size: ${bgSize}; background-position: ${bgPosition}; background-repeat: ${bgRepeat};`;
  }

  // For Outlook, we need VML for background images
  if (row.backgroundImage) {
    return `          <tr>
            <td class="email-row dark-mode-bg" style="${backgroundStyle} padding: ${row.padding}px;">
              <!--[if gte mso 9]>
              <v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" style="width:${contentWidth}px;">
                <v:fill type="tile" src="${row.backgroundImage}" />
                <v:textbox style="mso-fit-shape-to-text:true" inset="0,0,0,0">
              <![endif]-->
              <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                <tr>
${columnsHtml}
                </tr>
              </table>
              <!--[if gte mso 9]>
                </v:textbox>
              </v:rect>
              <![endif]-->
            </td>
          </tr>`;
  }

  return `          <tr>
            <td class="email-row dark-mode-bg" style="${backgroundStyle} padding: ${row.padding}px;">
              <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                <tr>
${columnsHtml}
                </tr>
              </table>
            </td>
          </tr>`;
}

/**
 * Generate a single column
 */
function generateColumn(col: EmailColumn, totalColumns: number, contentWidth: number): string {
  const columnWidth = Math.floor((contentWidth * col.width) / 100);
  const blocksHtml = col.blocks.map(block => generateBlock(block)).join('\n');

  // For single column, use 100% width
  if (totalColumns === 1) {
    return `                  <td style="vertical-align: top;">
${blocksHtml || '                    <!-- Empty column -->'}
                  </td>`;
  }

  return `                  <td width="${col.width}%" style="vertical-align: top; width: ${col.width}%;">
${blocksHtml || '                    <!-- Empty column -->'}
                  </td>`;
}

/**
 * Generate a content block
 */
function generateBlock(block: ContentBlock): string {
  switch (block.type) {
    case 'text':
      return generateTextBlock(block);
    case 'image':
      return generateImageBlock(block);
    case 'button':
      return generateButtonBlock(block);
    case 'divider':
      return generateDividerBlock(block);
    case 'spacer':
      return generateSpacerBlock(block);
    case 'footer':
      return generateFooterBlock(block);
    case 'vaccine_table':
      return generateVaccineTableBlock(block);
    default:
      return '                    <!-- Unknown block type -->';
  }
}

/**
 * Map font weight value to CSS number
 */
function getFontWeightValue(weight: string): number {
  switch (weight) {
    case 'normal': return 400;
    case 'medium': return 500;
    case 'semibold': return 600;
    case 'bold': return 700;
    default: return 400;
  }
}

/**
 * Generate text block HTML
 */
function generateTextBlock(block: TextBlock): string {
  const headingLevel = block.headingLevel || 'p';
  const fontFamily = block.fontFamily || 'Arial';
  const letterSpacing = block.letterSpacing || 0;
  const linkColor = block.linkColor || '#1e40af';
  const fontWeight = getFontWeightValue(block.fontWeight || 'normal');
  
  // Build the style string
  const styleProps = [
    `font-family: ${fontFamily}, sans-serif`,
    `font-size: ${block.fontSize}px`,
    `font-weight: ${fontWeight}`,
    `text-align: ${block.textAlign}`,
    `color: ${block.color}`,
    `line-height: ${block.lineHeight}`,
    letterSpacing > 0 ? `letter-spacing: ${letterSpacing}px` : null,
    'margin: 0',
  ].filter(Boolean).join('; ');

  // Escape HTML and convert newlines to <br> tags for email rendering
  const escapedContent = escapeHtml(block.content).replace(/\n/g, '<br>');

  // Use semantic tag based on heading level
  const content = headingLevel === 'p' 
    ? `<p style="${styleProps}">${escapedContent}</p>`
    : `<${headingLevel} style="${styleProps}">${escapedContent}</${headingLevel}>`;

  const bgStyle = block.backgroundColor ? `background-color: ${block.backgroundColor};` : '';
  
  // Add link color as inline style for any links that might be added via template variables
  return `                    <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                      <tr>
                        <td style="padding: 8px; ${bgStyle}">
                          <style>td a { color: ${linkColor}; }</style>
                          ${content}
                        </td>
                      </tr>
                    </table>`;
}

/**
 * Generate image block HTML
 */
function generateImageBlock(block: ImageBlock): string {
  // Build width style from new widthValue/widthUnit properties
  const widthStyle = block.widthValue ? `width: ${block.widthValue}${block.widthUnit};` : 'max-width: 100%;';
  const heightStyle = block.heightValue ? `height: ${block.heightValue}${block.heightUnit};` : 'height: auto;';
  const imgStyle = `${widthStyle} ${heightStyle} display: block;`;
  const alignStyle = block.align === 'center' ? 'margin: 0 auto;' : block.align === 'right' ? 'margin-left: auto;' : '';
  
  const imgTag = `<img src="${escapeHtml(block.src)}" alt="${escapeHtml(block.alt)}" style="${imgStyle} ${alignStyle}" />`;
  
  const content = block.link 
    ? `<a href="${escapeHtml(block.link)}" target="_blank">${imgTag}</a>`
    : imgTag;

  const bgStyle = block.backgroundColor ? `background-color: ${block.backgroundColor};` : '';
  
  return `                    <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                      <tr>
                        <td style="padding: 8px; text-align: ${block.align}; ${bgStyle}">
                          ${content}
                        </td>
                      </tr>
                    </table>`;
}

/**
 * Generate button block HTML
 */
function generateButtonBlock(block: ButtonBlock): string {
  // Use table-based button for better email client compatibility
  // Use inline bgcolor attribute for Outlook dark mode support
  return `                    <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                      <tr>
                        <td align="${block.align}" style="padding: 8px;">
                          <table role="presentation" border="0" cellspacing="0" cellpadding="0">
                            <tr>
                              <td class="dark-mode-bg" bgcolor="${block.backgroundColor}" style="background-color: ${block.backgroundColor}; border-radius: ${block.borderRadius}px; mso-padding-alt: ${block.padding.vertical}px ${block.padding.horizontal}px;">
                                <a href="${escapeHtml(block.link)}" target="_blank" style="display: inline-block; padding: ${block.padding.vertical}px ${block.padding.horizontal}px; font-size: 15px; font-weight: 600; color: ${block.textColor}; text-decoration: none; mso-line-height-rule: exactly;">
                                  ${escapeHtml(block.text)}
                                </a>
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </table>`;
}

/**
 * Generate divider block HTML
 */
function generateDividerBlock(block: DividerBlock): string {
  const bgStyle = block.backgroundColor ? `background-color: ${block.backgroundColor};` : '';
  
  return `                    <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                      <tr>
                        <td style="padding: 16px 8px; ${bgStyle}">
                          <table role="presentation" width="${block.width}%" border="0" cellspacing="0" cellpadding="0" align="center">
                            <tr>
                              <td style="border-top: ${block.thickness}px ${block.style} ${block.color};"></td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </table>`;
}

/**
 * Generate spacer block HTML
 */
function generateSpacerBlock(block: SpacerBlock): string {
  return `                    <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                      <tr>
                        <td style="height: ${block.height}px; line-height: ${block.height}px; font-size: 1px;">&nbsp;</td>
                      </tr>
                    </table>`;
}

/**
 * Generate footer block HTML
 */
function generateFooterBlock(block: FooterBlock): string {
  const socialIconsHtml: string[] = [];
  
  if (block.showSocialIcons) {
    if (block.socialLinks.facebook) {
      socialIconsHtml.push(`<a href="${escapeHtml(block.socialLinks.facebook)}" target="_blank" style="display: inline-block; margin: 0 8px;"><img src="https://cdn-icons-png.flaticon.com/32/733/733547.png" alt="Facebook" width="24" height="24" style="display: block;" /></a>`);
    }
    if (block.socialLinks.instagram) {
      socialIconsHtml.push(`<a href="${escapeHtml(block.socialLinks.instagram)}" target="_blank" style="display: inline-block; margin: 0 8px;"><img src="https://cdn-icons-png.flaticon.com/32/2111/2111463.png" alt="Instagram" width="24" height="24" style="display: block;" /></a>`);
    }
    if (block.socialLinks.twitter) {
      socialIconsHtml.push(`<a href="${escapeHtml(block.socialLinks.twitter)}" target="_blank" style="display: inline-block; margin: 0 8px;"><img src="https://cdn-icons-png.flaticon.com/32/5968/5968830.png" alt="X" width="24" height="24" style="display: block;" /></a>`);
    }
    if (block.socialLinks.linkedin) {
      socialIconsHtml.push(`<a href="${escapeHtml(block.socialLinks.linkedin)}" target="_blank" style="display: inline-block; margin: 0 8px;"><img src="https://cdn-icons-png.flaticon.com/32/733/733561.png" alt="LinkedIn" width="24" height="24" style="display: block;" /></a>`);
    }
  }

  const logoStyles = block.logoAsAvatar
    ? `display: block; width: ${block.logoWidth}px; height: ${block.logoWidth}px; border-radius: 50%; object-fit: cover; border: 3px solid ${block.linkColor};`
    : `display: block; max-width: 100%; height: auto;`;
  
  const logoHtml = block.logoUrl 
    ? `<tr><td align="center" style="padding-bottom: 16px;"><img src="${escapeHtml(block.logoUrl)}" alt="Logo" width="${block.logoWidth}" style="${logoStyles}" /></td></tr>`
    : '';

  const socialHtml = socialIconsHtml.length > 0
    ? `<tr><td align="center" style="padding-bottom: 16px;">${socialIconsHtml.join('')}</td></tr>`
    : '';

  const contactHtml = (block.contactEmail || block.contactPhone)
    ? `<tr><td align="center" style="padding-bottom: 12px; font-size: 14px; color: ${block.textColor};">${
        block.contactPhone ? escapeHtml(block.contactPhone) : ''
      }${block.contactPhone && block.contactEmail ? ' | ' : ''}${
        block.contactEmail ? `<a href="mailto:${escapeHtml(block.contactEmail)}" style="color: ${block.linkColor};">${escapeHtml(block.contactEmail)}</a>` : ''
      }</td></tr>`
    : '';

  const addressHtml = block.addressText
    ? `<tr><td align="center" style="padding-bottom: 12px; font-size: 12px; color: ${block.textColor};">${escapeHtml(block.addressText)}</td></tr>`
    : '';

  // Always use the template variable for unsubscribe URL - it will be replaced by the backend with the actual tracking URL
  const unsubscribeUrl = block.unsubscribeUrl || '{{unsubscribe_url}}';
  const unsubscribeHtml = block.unsubscribeText
    ? `<tr><td align="center" style="padding-bottom: 12px; font-size: 12px;"><a href="${escapeHtml(unsubscribeUrl)}" style="color: ${block.linkColor}; text-decoration: underline;">${escapeHtml(block.unsubscribeText)}</a></td></tr>`
    : '';

  const legalHtml = block.legalText
    ? `<tr><td align="center" style="font-size: 11px; color: ${block.textColor}; opacity: 0.7;">${escapeHtml(block.legalText)}</td></tr>`
    : '';

  return `                    <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                      <tr>
                        <td style="background-color: ${block.backgroundColor}; padding: 24px 16px; text-align: center;">
                          <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                            ${logoHtml}
                            ${socialHtml}
                            ${contactHtml}
                            ${addressHtml}
                            ${unsubscribeHtml}
                            ${legalHtml}
                          </table>
                        </td>
                      </tr>
                    </table>`;
}

/**
 * Generate vaccine table block HTML
 * This generates a template placeholder that will be replaced by the backend
 * with actual vaccine data at send time.
 * 
 * Supports three vaccine list types:
 * - 'overdue': Shows overdue vaccines only (uses {{#overdue_vaccines}})
 * - 'scheduled': Shows scheduled/upcoming vaccines only (uses {{#scheduled_vaccines}})
 * - 'all': Shows all vaccines (uses {{#all_vaccines}})
 */
function generateVaccineTableBlock(block: VaccineTableBlock): string {
  const fontFamily = block.fontFamily || 'Arial';
  const fontSize = block.fontSize || 14;
  const vaccineListType = block.vaccineListType || 'overdue';
  const overdueTextColor = block.overdueTextColor || '#dc2626';
  
  // Determine which columns to show based on vaccine type
  const showDaysOverdueColumn = block.showDaysOverdue && vaccineListType !== 'scheduled';
  const showStatusColumn = block.showStatus && vaccineListType !== 'overdue';
  
  // Build header cells based on enabled columns and vaccine type
  const headerCells: string[] = [];
  if (block.showPetName) headerCells.push('Pet Name');
  if (block.showVaccineName) headerCells.push('Vaccine');
  if (block.showScheduledDate) {
    headerCells.push(vaccineListType === 'scheduled' ? 'Scheduled Date' : 'Due Date');
  }
  if (showStatusColumn) headerCells.push('Status');
  if (showDaysOverdueColumn) headerCells.push('Days Overdue');
  
  const headerHtml = headerCells.map(cell => 
    `<th style="padding: 12px 8px; text-align: left; font-weight: 600; border-bottom: 2px solid ${block.borderColor};">${cell}</th>`
  ).join('');

  // Determine the Mustache template tag based on vaccine type
  const templateTag = vaccineListType === 'scheduled' 
    ? 'scheduled_vaccines' 
    : vaccineListType === 'all' 
      ? 'all_vaccines' 
      : 'overdue_vaccines';

  // Build row template cells
  const rowCells: string[] = [];
  if (block.showPetName) {
    rowCells.push(`<td style="padding: 10px 8px; border-bottom: 1px solid ${block.borderColor};">{{pet_name}}</td>`);
  }
  if (block.showVaccineName) {
    rowCells.push(`<td style="padding: 10px 8px; border-bottom: 1px solid ${block.borderColor};">{{vaccine_name}}</td>`);
  }
  if (block.showScheduledDate) {
    rowCells.push(`<td style="padding: 10px 8px; border-bottom: 1px solid ${block.borderColor};">{{scheduled_date}}</td>`);
  }
  if (showStatusColumn) {
    // Status column with conditional styling for overdue items
    rowCells.push(`<td style="padding: 10px 8px; border-bottom: 1px solid ${block.borderColor}; {{#is_overdue}}color: ${overdueTextColor}; font-weight: 600;{{/is_overdue}}">{{status}}</td>`);
  }
  if (showDaysOverdueColumn) {
    rowCells.push(`<td style="padding: 10px 8px; border-bottom: 1px solid ${block.borderColor}; color: ${overdueTextColor}; font-weight: 600;">{{days_overdue}}</td>`);
  }

  // Include metadata comment for backend parsing
  const metadataComment = `<!-- VACCINE_TABLE_CONFIG: type=${vaccineListType}, rowBg=${block.rowBackgroundColor}, altRowBg=${block.alternateRowColor} -->`;

  return `                    <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0">
                      <tr>
                        <td style="padding: 8px;">
                          ${block.title ? `<h3 style="font-family: ${fontFamily}, sans-serif; font-size: ${fontSize + 4}px; font-weight: 600; color: ${block.textColor}; margin: 0 0 12px 0;">${escapeHtml(block.title)}</h3>` : ''}
                          ${metadataComment}
                          <!-- VACCINE_TABLE_START -->
                          <table role="presentation" width="100%" border="0" cellspacing="0" cellpadding="0" style="font-family: ${fontFamily}, sans-serif; font-size: ${fontSize}px; color: ${block.textColor}; border: 1px solid ${block.borderColor}; border-collapse: collapse;">
                            <thead>
                              <tr style="background-color: ${block.headerBackgroundColor}; color: ${block.headerTextColor};">
                                ${headerHtml}
                              </tr>
                            </thead>
                            <tbody>
                              {{#${templateTag}}}
                              <tr style="background-color: {{row_bg_color}};">
                                ${rowCells.join('\n                                ')}
                              </tr>
                              {{/${templateTag}}}
                            </tbody>
                          </table>
                          <!-- VACCINE_TABLE_END -->
                        </td>
                      </tr>
                    </table>`;
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  const htmlEntities: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  
  return text.replace(/[&<>"']/g, char => htmlEntities[char] || char);
}

/**
 * Replace template variables with sample data for preview
 * Uses model_field naming pattern with backwards compatibility
 */
export function previewHtml(html: string, variables: Record<string, string> = {}): string {
  const defaults: Record<string, string> = {
    // New model_field pattern
    '{{practice_name}}': variables.practice_name || 'Your Practice',
    '{{practice_phone}}': '(555) 123-4567',
    '{{practice_email}}': 'info@yourpractice.com',
    '{{practice_address}}': '123 Main Street, Anytown, CA 94000',
    '{{unsubscribe_url}}': 'https://helppet.ai/unsubscribe/preview',
    '{{pet_owner_name}}': 'Jane Doe',
    '{{pet_owner_first_name}}': 'Jane',
    '{{pet_owner_email}}': 'jane.doe@email.com',
    '{{pet_owner_phone}}': '(555) 987-6543',
    '{{pet_name}}': 'Max',
    '{{owner_pet_name}}': 'Max',
    '{{has_have}}': 'has',
    '{{is/are}}': 'is',
    '{{is_are}}': 'is',
    '{{next_appointment_date}}': 'Friday, January 3rd at 2:00 PM',
    '{{appointment_date}}': 'Friday, January 3rd at 2:00 PM',  // Backwards compatibility
    '{{book_appointment_link}}': 'https://helppet.ai/book/ca/anytown/happy-paws-vet',
    // Legacy aliases for backwards compatibility
    '{{first_name}}': 'Jane',
    '{{name}}': 'Jane Doe',
    '{{email}}': 'jane.doe@email.com',
    '{{phone}}': '(555) 987-6543',
    '{{owner_name}}': 'Jane Doe',
  };

  let result = html;
  for (const [key, value] of Object.entries(defaults)) {
    result = result.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'gi'), value);
  }
  return result;
}

